import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import adsApi from './adsApi';

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      {sub ? <p className="mt-1 text-xs text-slate-500">{sub}</p> : null}
    </div>
  );
}

export default function AdsDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adsApi.dashboard();
      setData(res?.data || null);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingSpinner size="large" message="Loading ads dashboard..." />;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Brand Ads Dashboard</h1>
          <p className="text-sm text-slate-600">Campaign inventory, revenue, and engagement</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/ads/campaigns"
            className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            Manage campaigns
          </Link>
          <Link
            to="/ads/reports"
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Reports
          </Link>
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total campaigns" value={data?.total_campaigns ?? 0} />
        <StatCard label="Active" value={data?.active_campaigns ?? 0} />
        <StatCard label="Scheduled" value={data?.scheduled_campaigns ?? 0} />
        <StatCard label="Expired" value={data?.expired_campaigns ?? 0} />
        <StatCard label="Revenue" value={`₹${Number(data?.revenue || 0).toLocaleString('en-IN')}`} />
        <StatCard label="Impressions" value={Number(data?.impressions || 0).toLocaleString('en-IN')} />
        <StatCard label="Clicks" value={Number(data?.clicks || 0).toLocaleString('en-IN')} />
        <StatCard label="CTR" value={`${data?.ctr ?? 0}%`} sub={`Conv. rate ${data?.conversionRate ?? 0}%`} />
      </div>
    </div>
  );
}
